import { z } from "zod/v4"
import { VoiceId, VoiceIdSchema } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { UserDenormalizedWithLook, Users } from "./user"
// ─── 共通型 ──────────────────────────────────────────────────────────────────

export const VoiceFile = z.object({
  location: z.string().describe("音声ファイルの Storage パス。gs:// から始まる"),
  duration_ms: z.number().int().describe("音声の長さ（ミリ秒）"),
  updated_at: UpdatedAt.describe("音声ファイルの更新日時"),
})

export type VoiceFile = z.infer<typeof VoiceFile>

export const VoiceDenormalized = z.object({
  id: VoiceIdSchema.describe("元投稿のID"),
  title: z.string().describe("元投稿のタイトル"),
})

export type VoiceDenormalized = z.infer<typeof VoiceDenormalized>

export const VoiceReplyDenormalized = z.object({
  user: UserDenormalizedWithLook.describe("返信者の非正規化ユーザー情報"),
})

export type VoiceReplyDenormalized = z.infer<typeof VoiceReplyDenormalized>

export const VoiceRecentReply = z.object({
  last_replied_at: z.number().describe("最終返信時刻 (UNIX Timestamp mills)"),
  replies: z.array(VoiceReplyDenormalized).describe("最近の返信の非正規化情報一覧（上限N件）"),
})

export type VoiceRecentReply = z.infer<typeof VoiceRecentReply>

export const VoiceCounts = z.object({
  voice_reply_count: z.number().int().describe("返信数"),
  power: z.number().int().describe("累積パワー"),
  playing_emote_counts: z.record(z.string(), z.number().int()).describe("再生中に送られたエモートの種別ごと累積カウント"),
  ending_emote_counts: z.record(z.string(), z.number().int()).describe("再生終了後に送られたエモートの種別ごと累積カウント"),
})

export type VoiceCounts = z.infer<typeof VoiceCounts>

export const VoiceDenormalizedTimeline = z.object({
  id: VoiceIdSchema.describe("元投稿のID"),
  title: z.string().describe("元投稿のタイトル"),
  user: UserDenormalizedWithLook.describe("投稿者の非正規化ユーザー情報"),
  file: VoiceFile.describe("音声ファイル情報"),
  counts: VoiceCounts.describe("集計情報（denormalize）"),
  recent_reply: VoiceRecentReply.optional().describe("最近の返信情報。返信がない場合は存在しない"),
  bgm_id: z.string().optional().describe("設定されたBGMのID。未設定の場合は存在しない"),
})

export type VoiceDenormalizedTimeline = z.infer<typeof VoiceDenormalizedTimeline>

// ─── VoiceDocument ───────────────────────────────────────────────────────────
// Path: users/{userId}/voices/{voiceId}

export const VoiceDocumentMeta = z.object({
  user: UserDenormalizedWithLook.describe("投稿者の非正規化ユーザー情報"),
  file: VoiceFile.describe("音声ファイル情報"),
  title: z.string().describe("投稿のタイトル"),
  counts: VoiceCounts.describe("集計情報（denormalize）"),
  recent_reply: VoiceRecentReply.optional().describe("最近の返信情報。返信がない場合は存在しない"),
  bgm_id: z.string().optional().describe("設定されたBGMのID。未設定の場合は存在しない"),
})

export type VoiceDocumentMeta = z.infer<typeof VoiceDocumentMeta>

export const VoiceDocumentBody = z.intersection(
  VoiceDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type VoiceDocumentBody = z.infer<typeof VoiceDocumentBody>

export const VoiceDocument = z.intersection(
  VoiceDocumentBody,
  z.object({
    id: VoiceIdSchema,
  })
)

export type VoiceDocument = z.infer<typeof VoiceDocument>

export class VoiceDocuments {

  static toDocumentData(voice: VoiceDocument): object {
    return {
      ...voice,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): VoiceDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    const counts = data["counts"] ?? {}
    const recentReply = data["recent_reply"]
    return {
      id: VoiceId(doc.id),
      user: Users.parseDenormalizedWithLook(data["user"]),
      file: data["file"],
      title: data["title"],
      counts: {
        voice_reply_count: counts["voice_reply_count"] ?? 0,
        power: counts["power"] ?? 0,
        playing_emote_counts: counts["playing_emote_counts"] ?? {},
        ending_emote_counts: counts["ending_emote_counts"] ?? {},
      },
      recent_reply: recentReply ? VoiceDocuments.parseRecentReply(recentReply) : undefined,
      bgm_id: data["bgm_id"] ?? undefined,
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static parseRecentReply(data: Record<string, any>): VoiceRecentReply {
    return {
      last_replied_at: data["last_replied_at"],
      replies: (data["replies"] ?? []).map(VoiceDocuments.parseReplyDenormalized),
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static parseReplyDenormalized(data: Record<string, any>): VoiceReplyDenormalized {
    return {
      user: Users.parseDenormalizedWithLook(data["user"]),
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static parseDenormalizedTimeline(data: Record<string, any>): VoiceDenormalizedTimeline {
    const counts = data["counts"] ?? {}
    const recentReply = data["recent_reply"]
    return {
      id: VoiceId(data["id"]),
      title: data["title"] ?? "",
      user: Users.parseDenormalizedWithLook(data["user"]),
      file: data["file"],
      counts: {
        voice_reply_count: counts["voice_reply_count"] ?? 0,
        power: counts["power"] ?? 0,
        playing_emote_counts: counts["playing_emote_counts"] ?? {},
        ending_emote_counts: counts["ending_emote_counts"] ?? {},
      },
      recent_reply: recentReply ? VoiceDocuments.parseRecentReply(recentReply) : undefined,
      bgm_id: data["bgm_id"] ?? undefined,
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static parseDenormalized(data: Record<string, any>): VoiceDenormalized {
    return {
      id: VoiceId(data["id"]),
      title: data["title"] ?? "",
    }
  }

}

// ─── API Request/Response ────────────────────────────────────────────────────

export const CreateVoiceRequest = z.object({
  title: z.string().max(100).describe("投稿のタイトル（最大100文字）"),
  audio_location: z.string().describe("アップロード済み音声ファイルのStorageパス"),
  duration_ms: z.number().int().min(1).max(30000).describe("音声の長さ（ミリ秒、最大30秒）"),
  bgm_id: z.string().max(32).optional().describe("設定するBGMのID（任意）"),
})
export type CreateVoiceRequest = z.infer<typeof CreateVoiceRequest>

export const CreateVoiceResponse = z.object({
  id: z.string().describe("作成されたボイス投稿のID"),
})
export type CreateVoiceResponse = z.infer<typeof CreateVoiceResponse>

export const UpdateVoiceRequest = z.object({
  title: z.string().max(100).optional().describe("投稿のタイトル（最大100文字）"),
  bgm_id: z.string().max(32).nullable().optional().describe("設定するBGMのID。nullでBGM解除"),
})
export type UpdateVoiceRequest = z.infer<typeof UpdateVoiceRequest>
